// One journal day, at both grains: the day's record and the captures it was
// filled from, with the photos and their vision output.
//
// The day's record is editable here — amend, sign off, delete — through the
// same JournalsService rules the console has (an incomplete day refuses
// sign-off). The captures are not: a note is immutable once filed, for the
// admin exactly as for a tenant user (architecture/0027).
import { useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { adminJournalPhotoThumbnailPath } from "@gb/admin-contracts";
import { WEATHER_LABELS, WeatherSchema, type JournalEntry, type Weather } from "@gb/contracts";
import { AuthedImage } from "@/components/gb/authed-file";
import { EmptyState } from "@/components/gb/empty-state";
import { ErrorDialog } from "@/components/gb/error-dialog";
import { Field } from "@/components/gb/field";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  useApproveJournalEntry,
  useCreateJournalEntry,
  useDeleteJournalEntry,
  useDeleteJournalPhoto,
  useJournalEntry,
  useJournalNotes,
  useJournalPhotos,
  usePatchJournalEntry,
  useUploadJournalPhoto,
} from "./journal-api";

/**
 * The day's photographs, and the way in for new ones — the console's
 * single-PUT chain, thumbnail made here in the browser. Rendered whenever the
 * day exists (not only when photos do), because the empty state is where the
 * Add button has to live.
 */
function PhotoSection({
  tenantId,
  projectId,
  date,
  entryId,
}: {
  tenantId: string;
  projectId: string;
  date: string;
  entryId: string;
}) {
  const photos = useJournalPhotos(tenantId, projectId, date);
  const upload = useUploadJournalPhoto(tenantId, projectId);
  const remove = useDeleteJournalPhoto(tenantId, projectId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [queued, setQueued] = useState(0);

  const addFiles = async (files: FileList): Promise<void> => {
    // Sequential rather than parallel: each upload is three calls plus the
    // bytes, and a dozen at once would race the presign TTLs on a slow link.
    setQueued(files.length);
    for (const file of Array.from(files)) {
      await upload.mutateAsync({ entryId, file }).catch(() => undefined);
      setQueued((n) => n - 1);
    }
  };

  return (
    <>
      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-display text-[0.85rem] font-semibold tracking-[0.08em] text-muted uppercase">
          Photos
        </h2>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <Button size="sm" disabled={queued > 0} onClick={() => fileInput.current?.click()}>
          {queued > 0 ? `Uploading… (${queued} left)` : "Add photos"}
        </Button>
      </div>
      <ErrorDialog
        title="That photo didn’t upload"
        error={upload.isError ? String(upload.error.message) : remove.isError ? String(remove.error.message) : null}
      />
      {photos.data && photos.data.length > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-4">
          {photos.data.map((photo) => (
            <figure key={photo.id} className="rounded-lg border border-border-inner bg-white p-2">
              <AuthedImage
                path={adminJournalPhotoThumbnailPath(tenantId, projectId, photo.entryId, photo.id)}
                alt={photo.caption ?? photo.description ?? "site photo"}
                className="h-32 w-full rounded object-cover"
              />
              <figcaption className="mt-1 flex items-baseline justify-between gap-2">
                <span className="truncate text-[0.7rem] text-muted">
                  {photo.caption ?? photo.description ?? "—"}
                </span>
                <button
                  type="button"
                  className="shrink-0 cursor-pointer text-[0.68rem] text-danger hover:underline"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (!window.confirm("Delete this photo, bytes included?")) return;
                    remove.mutate({ entryId: photo.entryId, photoId: photo.id });
                  }}
                >
                  Delete
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-[0.8rem] text-muted">No photos for this day.</p>
      )}
    </>
  );
}

function DayEditor({
  tenantId,
  projectId,
  entry,
}: {
  tenantId: string;
  projectId: string;
  entry: JournalEntry;
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [headcount, setHeadcount] = useState(entry.headcount?.toString() ?? "");
  const [weather, setWeather] = useState<Weather | "">(entry.weather ?? "");
  const [workCompleted, setWorkCompleted] = useState(entry.workCompleted ?? "");
  const [sentTo, setSentTo] = useState("");
  const patch = usePatchJournalEntry(tenantId, projectId);
  const approve = useApproveJournalEntry(tenantId, projectId);
  const remove = useDeleteJournalEntry(tenantId, projectId);

  const error = patch.isError
    ? String(patch.error.message)
    : approve.isError
      ? String(approve.error.message)
      : remove.isError
        ? String(remove.error.message)
        : null;

  const save = (): void => {
    if (patch.isPending) return;
    patch.mutate(
      {
        entryId: entry.id,
        patch: {
          // Explicit nulls, not undefined: clearing a field is a statement a
          // PATCH can only make with null (the contract's nullish reasoning).
          headcount: headcount === "" ? null : Number(headcount),
          weather: weather === "" ? null : weather,
          workCompleted: workCompleted.trim() === "" ? null : workCompleted.trim(),
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <>
      <ErrorDialog error={error} />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => setEditing((v) => !v)}>
          {editing ? "Close editor" : "Edit day"}
        </Button>
        {!entry.approved && (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (approve.isPending) return;
              approve.mutate({
                entryId: entry.id,
                sentTo: sentTo
                  .split(",")
                  .map((address) => address.trim())
                  .filter(Boolean),
              });
            }}
          >
            <Input
              value={sentTo}
              onChange={(e) => setSentTo(e.target.value)}
              placeholder="sent to (emails, optional)"
              className="w-[240px]"
              aria-label="Sign-off recipients"
            />
            <Button type="submit" variant="success" size="sm" disabled={approve.isPending}>
              {approve.isPending ? "Signing off…" : "Sign off"}
            </Button>
          </form>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-danger"
          disabled={remove.isPending}
          onClick={() => {
            // A day's record and every photograph on it — worth a beat.
            if (!window.confirm(`Delete the record for ${entry.date}, photos included?`)) return;
            remove.mutate(entry.id, {
              onSuccess: () => void navigate(`/tenants/${tenantId}/projects/${projectId}`),
            });
          }}
        >
          Delete day
        </Button>
      </div>

      {editing && (
        <form
          className="mt-4 space-y-4 rounded-lg border border-border bg-white p-4"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <div className="grid grid-cols-2 gap-4">
            <Field label="Headcount" optional>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={2000}
                  value={headcount}
                  onChange={(e) => setHeadcount(e.target.value)}
                />
              )}
            </Field>
            <Field label="Weather" optional>
              {(props) => (
                <select
                  {...props}
                  value={weather}
                  onChange={(e) => setWeather(e.target.value as Weather | "")}
                  className="min-h-[46px] rounded-input border-[1.5px] border-border bg-white px-3 text-[0.9rem] text-ink"
                >
                  <option value="">—</option>
                  {WeatherSchema.options.map((value) => (
                    <option key={value} value={value}>
                      {WEATHER_LABELS[value]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          <Field label="Work completed" optional>
            {(props) => (
              <Textarea
                {...props}
                value={workCompleted}
                onChange={(e) => setWorkCompleted(e.target.value)}
                rows={4}
              />
            )}
          </Field>
          <Button type="submit" size="sm" disabled={patch.isPending}>
            {patch.isPending ? "Saving…" : "Save day"}
          </Button>
        </form>
      )}
    </>
  );
}

function OpenDay({
  tenantId,
  projectId,
  date,
}: {
  tenantId: string;
  projectId: string;
  date: string;
}) {
  const create = useCreateJournalEntry(tenantId, projectId);
  return (
    <EmptyState
      icon={CalendarDays}
      title="No entry for this day"
      description="The day was never opened."
      action={
        <Button size="sm" disabled={create.isPending} onClick={() => create.mutate({ date })}>
          {create.isPending ? "Opening…" : "Open this day"}
        </Button>
      }
    />
  );
}

export function JournalDayPage() {
  const { tenantId = "", projectId = "", date = "" } = useParams();
  const entry = useJournalEntry(tenantId, projectId, date);
  const notes = useJournalNotes(tenantId, projectId, date);

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link
          to={`/tenants/${tenantId}/projects/${projectId}`}
          className="text-brand hover:underline"
        >
          ← Project
        </Link>
      </div>
      <PageHeader eyebrow="Journal" title={date} />

      {entry.isError ? (
        <OpenDay tenantId={tenantId} projectId={projectId} date={date} />
      ) : entry.data ? (
        <div className="mt-4 rounded-lg border border-border bg-white p-4">
          <div className="font-mono text-[0.72rem] text-faint">
            headcount {entry.data.headcount ?? "—"} · weather {entry.data.weather ?? "—"} ·{" "}
            {entry.data.approved ? "signed off" : "not signed off"}
          </div>
          {entry.data.workCompleted && (
            <p className="mt-2 text-[0.85rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
              {entry.data.workCompleted}
            </p>
          )}
          {entry.data.deliveries.length > 0 && (
            <div className="mt-3">
              <div className="text-[0.72rem] font-semibold tracking-wide text-muted uppercase">
                Deliveries
              </div>
              {entry.data.deliveries.map((delivery, i) => (
                <div key={i} className="mt-1 text-[0.8rem] text-ink-prose">
                  {[delivery.supplier, delivery.item, delivery.qty, delivery.condition]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              ))}
            </div>
          )}
          {entry.data.incidents.length > 0 && (
            <div className="mt-3">
              <div className="text-[0.72rem] font-semibold tracking-wide text-danger uppercase">
                Incidents
              </div>
              {entry.data.incidents.map((incident, i) => (
                <div key={i} className="mt-1 text-[0.8rem] text-ink-prose">
                  {incident.text} ({incident.severity})
                </div>
              ))}
            </div>
          )}
          <DayEditor
            key={entry.data.updatedAt}
            tenantId={tenantId}
            projectId={projectId}
            entry={entry.data}
          />
        </div>
      ) : null}

      <h2 className="mt-8 font-display text-[0.85rem] font-semibold tracking-[0.08em] text-muted uppercase">
        Captures
      </h2>
      <div className="mt-3 space-y-3">
        {(notes.data ?? []).map((note) => (
          <div key={note.id} className="rounded-lg border border-border-inner bg-white p-4">
            <div className="font-mono text-[0.7rem] text-faint">
              {note.kind}
              {note.isDailyEntry ? " · daily log" : ""} · extraction {note.extractionStatus}
              {note.dictationId ? " · from dictation" : ""}
            </div>
            <p className="mt-1 text-[0.82rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
              {note.body ?? "(no text)"}
            </p>
          </div>
        ))}
        {notes.data?.length === 0 && (
          <p className="text-[0.8rem] text-muted">No captures for this day.</p>
        )}
      </div>

      {entry.data && (
        <PhotoSection
          tenantId={tenantId}
          projectId={projectId}
          date={date}
          entryId={entry.data.id}
        />
      )}
    </div>
  );
}
